// F7 / FR-N7: the topbar bell — DS IconButton with an unread count, opening the light DS Menu
// as the prototype's notification list (ui_kits/grackle-app/NotificationsScreen.jsx): a
// lavender icon disc per kind, the text, a relative lowercase time, the unread wash and dot.
// Shows the newest BELL_LIMIT; clicking a row marks it read and opens its link, and "See all"
// below them opens the notifications page with the whole feed.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Callout, IconButton, Menu, MenuItem } from "@grackle/ui";
import { notificationsPath } from "../library/routes";
import { NotificationRow } from "./NotificationRow";
import { notificationPath } from "./display";
import { useNotifications } from "./useNotifications";
import "./notifications.css";

/** The bell lists only the newest notifications; the rest are a "See all" away. */
export const BELL_LIMIT = 20;

export function NotificationBell() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { items, unread, askPermission, markRead, markAllRead, enableOsNotifications, dismissAsk } = useNotifications(
    (path) => navigate(path),
  );

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = unread > 0 ? `Notifications, ${unread} unread` : "Notifications";

  return (
    <div className="notif-bell" ref={rootRef}>
      <IconButton
        icon="bell"
        label={label}
        variant="outline"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      {unread > 0 ? (
        <Badge tone="record" size="sm" className="notif-bell__count gk-num" data-testid="notif-unread" aria-hidden="true">
          {unread > 9 ? "9+" : unread}
        </Badge>
      ) : null}
      {open ? (
        <Menu
          className="notif-bell__pop"
          header={
            <div className="notif-bell__head">
              <span className="notif-bell__title">Notifications</span>
              {unread > 0 ? (
                <Button variant="ghost" size="sm" onClick={markAllRead}>
                  Mark all read
                </Button>
              ) : null}
            </div>
          }
        >
          {askPermission ? (
            <div className="notif-bell__ask">
              <Callout tone="info" icon="bell">
                Get a desktop notification when something new arrives while Grackle is in the background? You can
                change this any time in your account settings.
              </Callout>
              <div className="notif-bell__ask-actions">
                <Button size="sm" onClick={() => void enableOsNotifications()}>
                  Turn on
                </Button>
                <Button size="sm" variant="ghost" onClick={dismissAsk}>
                  Not now
                </Button>
              </div>
            </div>
          ) : null}
          {items.length === 0 ? <div className="notif-bell__empty">You're all caught up.</div> : null}
          <div className="notif-list notif-list--popover">
            {items.slice(0, BELL_LIMIT).map((n) => (
              <MenuItem
                key={n.id}
                className={`notif-item${n.read_at ? "" : " is-unread"}`}
                disabled={n.recording?.state === "deleted"}
                onClick={() => {
                  if (!n.read_at) markRead(n.id);
                  setOpen(false);
                  const path = notificationPath(n);
                  if (path) navigate(path);
                }}
              >
                <NotificationRow notification={n} />
              </MenuItem>
            ))}
          </div>
          {slug ? (
            <div className="notif-bell__foot">
              <Link to={notificationsPath(slug)} onClick={() => setOpen(false)}>
                See all
              </Link>
            </div>
          ) : null}
        </Menu>
      ) : null}
    </div>
  );
}
